import { getCarvanaCareModalDeltas, getGapModalProps } from '|/helpers/carvana-care-modal-variants';
import currency from '|/helpers/currency';
import { formatApr } from '|/helpers/percent';
import { getStateNameFromAbbreviation } from '|/helpers/us-states';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import { deriveTradeIn, TradeInView } from './derive-trade-in';

export interface CoverageRow {
  label: string;
  coverage: string | null;
  amount: number;
  monthlyDelta: number | null;
  isDeclined: boolean;
}

export interface DueBreakdownRow {
  id: 'down' | 'shipping' | 'refund';
  title: string;
  amount: number;
}

export interface PriceDetailsView {
  vehicleTitle: string;
  vehicleTrim: string | null;

  showTaxSavings: boolean;
  taxSavingsCopy: string | null;
  showTitleAndRegistration: boolean;
  showShipping: boolean;

  showSelections: boolean;
  transferLabel: 'Delivery' | 'Pickup' | null;
  transferAmount: number | null;
  carvanaCare: CoverageRow | null;
  gap: CoverageRow | null;

  showTradeIn: boolean;
  tradeIn: TradeInView;
  /** Purchase total before the signed trade-in value is applied. */
  totalPurchasePrice: number;
  /** API purchase total, which already includes the signed trade-in value. */
  totalPriceWithTradeIn: number;

  showFinance: boolean;
  paymentTypeLabel: 'Carvana financing' | 'Self-arranged financing' | 'Cash' | null;
  lenderName: string | null;
  /** SAF only — the amount the third-party lender is financing. */
  financedWithLender: number | null;
  monthlyPayment: number | null;
  loanTermsCopy: string | null;
  cosignerNote: string | null;
  dueBreakdown: DueBreakdownRow[];
}

const normalized = (value?: string | null) => value?.toLowerCase() ?? '';

const isDeclined = (status?: string | null) => normalized(status) === 'declined';

// 'NotSet'/'Unavailable' means the coverage was never offered — mirrors the same
// exclusion in review-order's derive-review-order-view.ts and price-breakdown.tsx.
const isOffered = (status?: string | null) => {
  const value = normalized(status);
  return value !== '' && value !== 'notset' && value !== 'unavailable';
};

const hasTradeIn = (summary: ReviewSummaryResponse | null) => {
  const status = normalized(summary?.tradeIn?.status);
  return status !== '' && status !== 'notset' && status !== 'declined';
};

// A declined trade still renders the section — it swaps the breakdown for an
// "Add trade-in" prompt — so this is broader than
// hasTradeIn, which gates the money-moving bits.
const hasTradeInDecision = (summary: ReviewSummaryResponse | null) => {
  const status = normalized(summary?.tradeIn?.status);
  return status !== '' && status !== 'notset';
};

const formatThousands = (value: number) =>
  value % 1000 === 0 ? `${value / 1000}k` : value.toLocaleString('en-US');

const carvanaCareCoverage = (months?: number | null, mileage?: number | null) => {
  const parts = [
    months ? `${months} mo.` : null,
    mileage ? `+${formatThousands(mileage)} mi.` : null
  ].filter(Boolean);
  return parts.length ? `Covers you for ${parts.join(' or ')}` : null;
};

const buildCoverageRow = (
  status: string | null | undefined,
  label: string,
  coverage: string | null,
  amount: number,
  {
    monthlyDelta,
    downDelta,
    hasLoanComparison
  }: { monthlyDelta: number; downDelta: number; hasLoanComparison: boolean },
  isCarvanaFinancing: boolean
): CoverageRow | null => {
  if (!isOffered(status)) return null;

  const declined = isDeclined(status);

  return {
    label,
    coverage: declined ? null : coverage,
    amount,
    // Financing eligibility, not the sign of the payment delta, decides whether the
    // monthly line is valid. Extending the term can fully finance the product while
    // leaving the payment delta at zero (or below it), and SAF/cash orders should never
    // inherit a monthly line from stray comparison-loan data.
    monthlyDelta:
      !declined && isCarvanaFinancing && hasLoanComparison && downDelta <= 0 ? monthlyDelta : null,
    isDeclined: declined
  };
};

export const derivePriceDetails = (summary: ReviewSummaryResponse | null): PriceDetailsView => {
  const vehicle = summary?.vehicle;
  const fundingType = normalized(summary?.funding?.type);
  const isCarvanaFinancing =
    fundingType === 'financing' && normalized(summary?.funding?.institution) === 'carvana';
  const isSaf =
    fundingType === 'thirdparty' || (fundingType === 'financing' && !isCarvanaFinancing);
  const isCash = fundingType === 'cash';

  const activityType =
    normalized(summary?.transfer?.activityType) || normalized(summary?.transfer?.deliveryType);
  const isPickup = activityType.includes('pickup');
  const isDelivery = activityType.includes('delivery');

  const tradeInTaxSavings = summary?.totals?.tradeInTaxSavings ?? 0;
  const showTaxSavings = tradeInTaxSavings > 0 && hasTradeIn(summary);
  const stateName =
    getStateNameFromAbbreviation(summary?.customer?.address?.state ?? '') ?? 'your state';

  const noDeltas = { monthlyDelta: 0, downDelta: 0, hasLoanComparison: false };

  const carvanaCareRow = buildCoverageRow(
    summary?.product?.status,
    // Declined CarvanaCare drops the coverage type — the plan the customer turned down
    // isn't theirs to name (the design shows a bare "CarvanaCare®").
    summary?.product?.type && !isDeclined(summary?.product?.status)
      ? `CarvanaCare® ${summary.product.type}`
      : 'CarvanaCare®',
    carvanaCareCoverage(summary?.product?.months, summary?.product?.mileage),
    summary?.product?.price ?? 0,
    summary ? getCarvanaCareModalDeltas(summary) : noDeltas,
    isCarvanaFinancing
  );

  const gapRow = buildCoverageRow(
    summary?.gap?.status,
    'GAP coverage',
    'Covers you for the life of your loan',
    summary?.gap?.price ?? 0,
    summary ? getGapModalProps(summary) : noDeltas,
    isCarvanaFinancing
  );

  const loan = summary?.funding?.loan;
  // `funding.thirdParty` is `any` on ReviewSummaryResponse; narrow it to the two
  // breakdown fields this sheet reads rather than propagating `any`.
  const thirdParty = summary?.funding?.thirdParty as
    | { lenderName?: string; breakdown?: { cash?: number; loanAmount?: number } }
    | undefined;
  const amountFinanced = isSaf ? thirdParty?.breakdown?.loanAmount ?? 0 : loan?.amountFinanced ?? 0;
  const loanTermsCopy =
    isCarvanaFinancing && amountFinanced > 0 && loan?.apr != null && (loan?.term ?? 0) > 0
      ? `Total amount financed: ${currency(amountFinanced)} at ${formatApr(loan.apr)} APR for ${
          loan.term
        } months`
      : null;

  const cashDown = isSaf
    ? thirdParty?.breakdown?.cash ?? summary?.payment?.cashDown ?? 0
    : summary?.payment?.cashDown ?? 0;
  const hasShippingFee = (summary?.totals?.shippingFee ?? 0) > 0;
  // On a cash order the leading row is the whole order amount, so its label tracks what
  // the number actually excludes: "Subtotal" once shipping is a separate line below it,
  // "Total purchase price" when it is the only line.
  const cashDownTitle = isCash
    ? hasShippingFee
      ? 'Subtotal'
      : 'Total purchase price'
    : 'Cash down payment';
  const dueBreakdown = (
    [
      {
        id: 'down',
        title: cashDownTitle,
        amount: cashDown
      },
      {
        id: 'shipping',
        title: 'Nonrefundable shipping',
        amount: summary?.totals?.shippingFee ?? 0
      },
      {
        id: 'refund',
        title: 'Credit from previous order',
        amount: summary?.payment?.creditFromPreviousOrder ?? 0
      }
    ] as DueBreakdownRow[]
  ).filter(row => row.amount > 0);
  const totalPriceWithTradeIn = summary?.totals?.totalPrice ?? 0;
  const tradeInValue = (summary?.tradeIn?.value ?? 0) - (summary?.tradeIn?.lienAmount ?? 0);
  const totalPurchasePrice = totalPriceWithTradeIn + (hasTradeIn(summary) ? tradeInValue : 0);

  // Mirrors control's hasPaymentMethodBeenVerified (use-order-summary-vm.tsx): the section
  // waits for saved terms rather than rendering "$0/mo" placeholders between selecting a
  // payment type and saving the step.
  const hasCarvanaFinancingVerified =
    isCarvanaFinancing && !!loan?.apr && !!loan?.term && !!loan?.payment;
  const hasSafVerified = isSaf && !!thirdParty?.breakdown?.loanAmount && !!thirdParty?.lenderName;
  const hasCashVerified = isCash && cashDown > 0;
  const hasPaymentMethodBeenVerified =
    hasCarvanaFinancingVerified || hasSafVerified || hasCashVerified;

  return {
    vehicleTitle: [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' '),
    vehicleTrim: vehicle?.trim ?? null,

    showTaxSavings,
    taxSavingsCopy: showTaxSavings
      ? `You're saving ${currency(tradeInTaxSavings, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })} in ${stateName} sales tax by including your trade-in.`
      : null,
    showTitleAndRegistration: (summary?.totals?.fees ?? 0) > 0,
    showShipping: (summary?.totals?.shippingFee ?? 0) > 0,

    showSelections:
      isPickup || isDelivery || carvanaCareRow !== null || gapRow !== null || hasTradeIn(summary),
    transferLabel: isPickup ? 'Pickup' : isDelivery ? 'Delivery' : null,
    transferAmount: isDelivery ? summary?.totals?.deliveryCharge ?? 0 : null,
    carvanaCare: carvanaCareRow,
    gap: gapRow,

    showTradeIn: hasTradeInDecision(summary),
    tradeIn: deriveTradeIn({
      isDeclined: isDeclined(summary?.tradeIn?.status),
      offer: summary?.tradeIn?.value ?? 0,
      lienAmount: summary?.tradeIn?.lienAmount ?? 0,
      tradeDown: summary?.funding?.loan?.details?.tradeDown ?? 0,
      isTaxSavingsEligible: !!summary?.tradeIn?.isTradeInTaxSavingsEligible,
      stateName,
      lienHolder: summary?.tradeIn?.lienHolderName ?? null,
      isCarvanaFinanced: isCarvanaFinancing
    }),
    totalPurchasePrice,
    totalPriceWithTradeIn,

    showFinance: hasPaymentMethodBeenVerified,
    paymentTypeLabel: isCarvanaFinancing
      ? 'Carvana financing'
      : isSaf
        ? 'Self-arranged financing'
        : isCash
          ? 'Cash'
          : null,
    lenderName: isSaf ? thirdParty?.lenderName ?? null : null,
    financedWithLender: isSaf ? amountFinanced : null,
    monthlyPayment: hasCarvanaFinancingVerified ? loan?.payment ?? null : null,
    loanTermsCopy,
    // `<delivery>` in the design copy is a placeholder for the chosen transfer method.
    // Branches on isDelivery, not isPickup, to match control's how-youre-paying.tsx
    // when neither is set yet.
    cosignerNote: `Your co-signer must be present at the ${
      isDelivery ? 'delivery' : 'pickup'
    } appointment with a valid ID.`,
    dueBreakdown
  };
};

export default derivePriceDetails;
